import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import TypingPracticePage from './pages/TypingPracticePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import SettingsPage from './pages/SettingsPage';
import ProfessionChoicePage from './pages/ProfessionChoicePage';
import ComprehensiveGrammarPage from './pages/ComprehensiveGrammarPage';
import VocabularyPage from './pages/VocabularyPage';
import UnknownWordsPage from './pages/UnknownWordsPage';
import GoetheB2HubPage from './pages/exam/b2/GoetheB2HubPage';
import LesenPage from './pages/exam/b2/LesenPage';
import HoerenPage from './pages/exam/b2/HoerenPage';
import SchreibenPage from './pages/exam/b2/SchreibenPage';
import SprechenPage from './pages/exam/b2/SprechenPage';
import WritingJournalPage from './pages/WritingJournalPage';
import TimedChallengePage from './pages/TimedChallengePage';
import BookmarksPage from './pages/BookmarksPage';
import ChatPage from './pages/ChatPage';
import PlatformPresence from './components/PlatformPresence';
import { LanguageProvider } from './contexts/LanguageContext';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, emailConfirmed, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;

  // Block dashboard if email is not yet confirmed
  if (!emailConfirmed) {
    return <Navigate to="/login" state={{ needsConfirmation: true }} />;
  }

  return children;
};


// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/typing-practice"
        element={
          <ProtectedRoute>
            <TypingPracticePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/grammar"
        element={
          <ProtectedRoute>
            <ComprehensiveGrammarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profession"
        element={
          <ProtectedRoute>
            <ProfessionChoicePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vocabulary"
        element={
          <ProtectedRoute>
            <VocabularyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/unknown-words"
        element={
          <ProtectedRoute>
            <UnknownWordsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam/b2"
        element={
          <ProtectedRoute>
            <GoetheB2HubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam/b2/lesen"
        element={
          <ProtectedRoute>
            <LesenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam/b2/hoeren"
        element={
          <ProtectedRoute>
            <HoerenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam/b2/schreiben"
        element={
          <ProtectedRoute>
            <SchreibenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam/b2/sprechen"
        element={
          <ProtectedRoute>
            <SprechenPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="/timed-challenge" element={<ProtectedRoute><TimedChallengePage /></ProtectedRoute>} />
      <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/writing-journal" element={<ProtectedRoute><WritingJournalPage /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <PlatformPresence />
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
